import React, { useContext, useMemo, useState, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { Subscribe } from 'unstated';

import { Text } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { type GlobalShortcutsItem } from '@atlassian/global-shortcuts';
import { useTeams } from '@atlassian/global-shortcuts/teams';

import { UNSAFE_noExposureExp } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { CoreInvitesContext } from '@confluence/core-invites-provider/entry-points/CoreInvitesContext';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { CoreInvitesSource } from '@confluence/core-invites-provider/entry-points/constants';

import { CreateTeamModal } from '../TeamAppShortcutItemCreateTeamDialog';
import { useFallbackUrls } from '../../useFallbackUrls';

const ACTION_SUBJECT_ID = 'teamsAppShortcut';
const CREATE_A_TEAM = 'createATeam';
const INVITE_PEOPLE = 'invitePeople';

export const useTeamsAppShortcut = (): {
	config: GlobalShortcutsItem;
	component: React.ReactNode;
} => {
	const hasFiredExposure = useRef<boolean>(false);
	const fallbackUrls = useFallbackUrls();
	const shortcutUrl = fallbackUrls['teamsShortcut'];
	const teamsShortcut = useTeams(shortcutUrl);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const { cloudId } = useSessionData();
	const { openCoreInvites } = useContext(CoreInvitesContext);
	const [experimentConfig, fireExposure] = useMemo(
		() => UNSAFE_noExposureExp('confluence_global_nav_teams_app_with_actions'),
		[],
	);
	const cohort = useMemo(() => experimentConfig.get('cohort', 'not-enrolled'), [experimentConfig]);

	const [isCreateTeamModalOpen, setCreateTeamModalOpen] = useState(false);

	const config = useMemo(
		() => ({
			...teamsShortcut,
			onMouseEnter: () => {
				if (!hasFiredExposure.current) {
					hasFiredExposure.current = true;
					// Actions can't be dynamically shown so unsafe exposure used here on hover as the point where the actions are shown / not shown
					fireExposure();
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							source: 'globalNavigation',
							action: 'mouseEnter',
							actionSubject: 'navigationItem',
							actionSubjectId: ACTION_SUBJECT_ID,
							attributes: {
								inviteFromTeamsAppCohort: cohort,
							},
						},
					}).fire();
				}
			},
			...(cohort === 'variation'
				? {
						actions: [
							{
								onClick: () => {
									createAnalyticsEvent({
										type: 'sendUIEvent',
										data: {
											source: 'globalNavigation',
											action: 'clicked',
											actionSubject: 'dropdownItem',
											actionSubjectId: ACTION_SUBJECT_ID,
											attributes: {
												itemId: INVITE_PEOPLE,
											},
										},
									}).fire();
									openCoreInvites?.(
										CoreInvitesSource.PEOPLE_MENU,
										{
											onModalClosed: () => null,
											onInviteCompleted: () => null,
										},
										undefined,
										{
											resourceAri: `ari:cloud:confluence::site/${cloudId}`,
											source: ACTION_SUBJECT_ID,
										},
									);
								},
								component: <Text>{formatMessage(messages.invitePeople)}</Text>,
							},
							{
								onClick: () => {
									createAnalyticsEvent({
										type: 'sendUIEvent',
										data: {
											source: 'globalNavigation',
											action: 'clicked',
											actionSubject: 'dropdownItem',
											actionSubjectId: ACTION_SUBJECT_ID,
											attributes: {
												itemId: CREATE_A_TEAM,
											},
										},
									}).fire();
									setCreateTeamModalOpen(true);
								},
								component: <Text>{formatMessage(messages.createTeam)}</Text>,
							},
						],
					}
				: {}),
		}),
		[
			createAnalyticsEvent,
			formatMessage,
			setCreateTeamModalOpen,
			openCoreInvites,
			cloudId,
			teamsShortcut,
			fireExposure,
			cohort,
		],
	);

	const createTeamModalComponent = useMemo(() => {
		return (
			<Subscribe to={[FlagsStateContainer]}>
				{(flags: FlagsStateContainer) => (
					<CreateTeamModal
						isOpen={isCreateTeamModalOpen}
						onClose={() => setCreateTeamModalOpen(false)}
						addFlag={flags.showFlag}
					/>
				)}
			</Subscribe>
		);
	}, [isCreateTeamModalOpen, setCreateTeamModalOpen]);

	if (cohort !== 'variation') {
		return {
			config,
			component: null,
		};
	}

	return {
		config,
		component: createTeamModalComponent,
	};
};

const messages = defineMessages({
	invitePeople: {
		id: 'side-navigation.custom.teams-app-shortcut.invite-people',
		defaultMessage: 'Invite people',
		description: 'Label for the menu item that opens the invite people modal',
	},
	createTeam: {
		id: 'side-navigation.custom.teams-app-shortcut.create-team',
		defaultMessage: 'Create team',
		description: 'Label for the menu item that opens the create team modal',
	},
});
